import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  Inject,
  inject,
  input,
  NgZone,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Event as RouterEvent,
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  Router,
  RouterLink,
} from '@angular/router';
import { SenseiIdDirective } from '@examdojo/browser/sensei-id';
import { AvatarColor, AvatarInitial } from '@examdojo/core/avatar';
import { HapticDirective, HapticsService } from '@examdojo/core/haptics';
import { IconComponent } from '@examdojo/core/icon';
import { OperatingSystem, PlatformService } from '@examdojo/platform';
import { ProfileAvatarComponent } from '@examdojo/user';
import { TranslocoPipe } from '@jsverse/transloco';
import { distinctUntilChanged, filter, fromEvent, map, merge, startWith, tap } from 'rxjs';
import { injectNavSegment } from '../sidenav/nav-segment';
import { isNavItemActive, SidenavItem } from '../sidenav/sidenav.model';

export type TabBarProfileAppearance = 'default' | 'rail';

/**
 * How far a finger may travel between pressing a tab and letting go and still have tapped it:
 * Flutter's touch slop. A browser drops the click at about half of it, and a thumb reaching for
 * the foot of the screen rolls further than that.
 */
export const TAB_TAP_SLOP_PX = 18;

/** How long after the bar answered a touch the browser's own click for that touch may still arrive. */
const BROWSER_CLICK_WINDOW_MS = 1000;

/** On the host while the last pointer was a finger or a pen: what opens the tabs' reach past their boxes. */
export const TAB_BAR_TOUCH_CLASS = 'tab-bar--touch';

const TAB = '.tab-bar__tab';

interface TabPress {
  pointerId: number;
  tab: HTMLElement;
  x: number;
  y: number;
  /**
   * The browser took the press from the bar while the finger was still down. iOS does so for a
   * touch that lands while the page behind the bar is still gliding, and then sends no
   * `pointerup`, only the `touchend`.
   */
  cancelled: boolean;
}

function tabOf(target: EventTarget | null): HTMLElement | null {
  return target instanceof Element ? target.closest<HTMLElement>(TAB) : null;
}

/** The first moment after the current frame has been painted. */
function afterPaint(run: () => void): void {
  requestAnimationFrame(() => setTimeout(run));
}

/**
 * The navigation a tap started is over, one way or another, unless a newer one took its place: a
 * second tap, or the redirect a guard answered with, which settles in turn.
 */
function settles(event: RouterEvent): boolean {
  return (
    event instanceof NavigationEnd ||
    event instanceof NavigationError ||
    event instanceof NavigationSkipped ||
    (event instanceof NavigationCancel &&
      event.code !== NavigationCancellationCode.SupersededByNewNavigation &&
      event.code !== NavigationCancellationCode.Redirect)
  );
}

/**
 * The compact surface of the shell nav, natively and in a browser alike: a
 * floating pill over the page rather than a bar across its foot, so the content
 * keeps scrolling behind it (Figma 3212:259873).
 *
 * Both platforms are drawn, from one item list: iOS stacks a 26px icon over the
 * label inside the selected pill, Android puts a 24px icon in a pill of its own
 * and hangs the label below it. Everything else — the geometry, the colours, the
 * greying of an unselected illustration — is shared.
 *
 * The selection is one element that slides, not a background that moves from
 * tab to tab: the tabs share the pill's width equally, so the indicator is
 * `100% / count` wide and travels by whole multiples of itself. That is a
 * transform and nothing else, which is what keeps the move on the compositor.
 */
@Component({
  selector: 'dojo-tab-bar',
  standalone: true,
  imports: [RouterLink, TranslocoPipe, IconComponent, HapticDirective, SenseiIdDirective, ProfileAvatarComponent],
  templateUrl: './tab-bar.component.html',
  styleUrl: './tab-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Over the page and clear of the pointer: only the pill and its tabs' reach take
  // a touch, so the content either side of it on a tablet stays scrollable.
  // `bottom` is the stylesheet's, which keeps the bar at rest while the shell
  // follows the keyboard.
  host: {
    class: 'pointer-events-none absolute inset-x-0 z-2',
    '[class.tab-bar--android]': 'isAndroid',
  },
})
export class TabBarComponent {
  constructor(
    private readonly router: Router,
    private readonly haptics: HapticsService,
    private readonly zone: NgZone,
    private readonly host: ElementRef<HTMLElement>,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.settleTappedTab().pipe(takeUntilDestroyed()).subscribe();

    // Outside the zone: a pointer moving or pressing changes nothing Angular draws until a tab
    // is actually tapped, and every touch in the app would otherwise run change detection.
    const pointers = merge(this.followPointerKind(), this.answerTouches(), this.screenClicks()).pipe(
      takeUntilDestroyed(),
    );
    this.zone.runOutsideAngular(() => pointers.subscribe());
  }

  private readonly platformService = inject(PlatformService);

  readonly items = input.required<SidenavItem[]>();
  /** The tab drawn as the signed-in user's avatar rather than an illustration. */
  readonly profileRoute = input<string | null>(null);
  /** `rail` draws the avatar as a person on the rail is drawn: a bold initial. */
  readonly profileAppearance = input<TabBarProfileAppearance>('default');
  /** The avatar's colour wherever it is not greyed. */
  readonly profileColor = input<AvatarColor>('name');

  readonly isAndroid = this.platformService.operatingSystem === OperatingSystem.Android;

  readonly iconSize = this.isAndroid ? 24 : 26;

  private readonly segment = injectNavSegment();

  /** The tab tapped last, from the tap until the navigation it started has settled. */
  private readonly tapped = signal<number | null>(null);

  /**
   * Which tab the indicator stands over, or `-1` where the page under the bar
   * belongs to none of them and it is not drawn.
   *
   * The tab tapped, as soon as it is tapped: the URL only says so once the next
   * page is loaded and built, and a bar that sits still until then reads as a
   * tap that missed, and is tapped again.
   *
   * Otherwise the URL segment rather than `routerLinkActive`: that one publishes
   * a class onto the anchor, and the indicator is a sibling of all five. The same
   * index then colours the tab, so the pill and the label can never disagree.
   */
  readonly activeIndex = computed(
    () => this.tapped() ?? this.items().findIndex((item) => isNavItemActive(item, this.segment())),
  );

  readonly profileAvatarInitial = computed<AvatarInitial>(() =>
    this.profileAppearance() === 'rail' ? 'display' : 'fitted',
  );

  private press: TabPress | null = null;

  /** The click the bar dispatched itself for a touch it answered. */
  private ownClick: MouseEvent | null = null;

  /**
   * Until when the browser's own click for the touch last answered is dropped. The next press ends it
   * too: a browser clicks for a touch before another can come down, and a touch it never clicks for
   * would otherwise leave the next click on a tab, a trackpad's, to be dropped in its place.
   */
  private browserClickDeadline = 0;

  labelKey(item: SidenavItem): string {
    return this.platformService.isPhoneLayout() ? (item.labelKeyPhone ?? item.labelKey) : item.labelKey;
  }

  /**
   * Lets go of the tapped tab once its navigation is over: arrived, and the URL
   * says the same, or refused, and the indicator goes back to the page still on
   * screen.
   */
  private settleTappedTab() {
    return this.router.events.pipe(
      filter(settles),
      tap(() => this.tapped.set(null)),
    );
  }

  /**
   * The tabs reach past their boxes for a finger or a pen alone. A mouse or a
   * trackpad points where it means to, and a click beside a tab taking it would
   * feel wrong. It always moves before it clicks, so the reach is gone before its
   * click lands; a finger has no move ahead of it, so the first touch after a
   * trackpad still finds the tabs at their own size.
   */
  private followPointerKind() {
    const touchFirst = this.document.defaultView?.matchMedia?.('(pointer: coarse)').matches ?? false;
    return merge(
      fromEvent<PointerEvent>(this.document, 'pointermove', { capture: true, passive: true }),
      fromEvent<PointerEvent>(this.document, 'pointerdown', { capture: true, passive: true }),
    ).pipe(
      map((event) => event.pointerType !== 'mouse'),
      startWith(touchFirst),
      distinctUntilChanged(),
      tap((touch) => this.host.nativeElement.classList.toggle(TAB_BAR_TOUCH_CLASS, touch)),
    );
  }

  /**
   * Answers a tap on a tab from the touch itself rather than from the browser's
   * click, which a browser drops once the finger has travelled further than its
   * own slop, or when the touch stopped a glide. Every touch let go within
   * {@link TAB_TAP_SLOP_PX} of where it came down taps the tab it came down on,
   * whether or not the browser cancelled it on the way. Only a cancelled touch
   * (`touchcancel`, the system taking the gesture) taps nothing.
   */
  private answerTouches() {
    const host = this.host.nativeElement;
    return merge(
      fromEvent<PointerEvent>(host, 'pointerdown').pipe(tap((event) => this.pressTab(event))),
      fromEvent<PointerEvent>(host, 'pointerup').pipe(tap((event) => this.releaseTab(event))),
      fromEvent<PointerEvent>(host, 'pointercancel').pipe(tap(() => this.press && (this.press.cancelled = true))),
      fromEvent<TouchEvent>(host, 'touchend', { passive: true }).pipe(tap((event) => this.liftCancelledPress(event))),
      fromEvent(host, 'touchcancel', { passive: true }).pipe(tap(() => (this.press = null))),
    );
  }

  /** Every click on a tab, the bar's own included; captured, so ahead of the tab's own handlers. */
  private screenClicks() {
    return fromEvent<MouseEvent>(this.host.nativeElement, 'click', { capture: true }).pipe(
      tap((event) => this.screenClick(event)),
    );
  }

  private pressTab(event: PointerEvent): void {
    this.browserClickDeadline = 0;
    const tab = event.pointerType === 'mouse' || !event.isPrimary ? null : tabOf(event.target);
    this.press = tab ? { pointerId: event.pointerId, tab, x: event.clientX, y: event.clientY, cancelled: false } : null;
  }

  private releaseTab(event: PointerEvent): void {
    const press = this.press;
    this.press = null;
    if (!press || press.cancelled || press.pointerId !== event.pointerId) {
      return;
    }
    this.answerWithin(press, event);
  }

  private liftCancelledPress(event: TouchEvent): void {
    const press = this.press;
    const [lift] = Array.from(event.changedTouches ?? []);
    if (!press?.cancelled || !lift) {
      return;
    }
    this.press = null;
    this.answerWithin(press, lift);
  }

  /**
   * Lights the tab on this frame and clicks it once that frame is painted. A page already built
   * swaps in inside the click's own task, which would hold the light back by as long as the page
   * takes to lay out; the slide, a transform, carries on while it does.
   */
  private answerWithin(press: TabPress, lift: { clientX: number; clientY: number }): void {
    if (Math.hypot(lift.clientX - press.x, lift.clientY - press.y) > TAB_TAP_SLOP_PX) {
      return;
    }

    const { tab } = press;
    this.browserClickDeadline = performance.now() + BROWSER_CLICK_WINDOW_MS;
    this.zone.run(() => this.tapped.set(this.indexOf(tab)));
    afterPaint(() => {
      if (!tab.isConnected) {
        return;
      }
      // A click rather than a call to the router, so the tab's link, its haptic and anything else
      // listening answer it as they answer any click.
      const click = new MouseEvent('click', { bubbles: true, cancelable: true, view: this.document.defaultView });
      this.ownClick = click;
      tab.dispatchEvent(click);
      this.ownClick = null;
    });
  }

  private indexOf(tab: HTMLElement): number {
    return Array.from(this.host.nativeElement.querySelectorAll(TAB)).indexOf(tab);
  }

  /**
   * Moves the indicator to the tab clicked, ahead of the navigation the click
   * starts, and drops the browser's own click for a touch the bar has already
   * answered, so one tap navigates once and ticks once.
   */
  private screenClick(event: MouseEvent): void {
    const tab = tabOf(event.target);
    if (!tab) {
      return;
    }

    // A click with no count is a keyboard's, never the browser's for a touch.
    if (event !== this.ownClick && event.detail > 0 && performance.now() < this.browserClickDeadline) {
      this.browserClickDeadline = 0;
      event.preventDefault();
      event.stopPropagation();
      this.haptics.silence(event);
      return;
    }

    // A modified click opens the tab elsewhere, if anywhere, and starts no navigation here to settle it.
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    // A touch lit its tab already; running the zone again would only check the whole app once more.
    const index = this.indexOf(tab);
    if (this.tapped() !== index) {
      this.zone.run(() => this.tapped.set(index));
    }
  }
}
